import { Auth0Provider } from '@auth0/auth0-react';
import { FC, ReactNode } from 'react';
import { trackException } from '../../utils/monitoring';
import { CirraServiceType } from '../../enums/auth';
import { AuthErrorBoundary } from '../ErrorBoundary/AuthErrorBoundary';
import { LS_KEY_CURRENT_ACCOUNT_ID } from '../../app/Slices/teamSlice';
import { useNavigate } from 'react-router-dom';
import { useRouting } from '../../hooks/useRouting';

interface CustomAuth0ProviderProps {
  service: CirraServiceType;
  children: ReactNode;
}

interface PostLoginState {
  returnTo?: string;
  signup_attribution?: {
    email?: string;
    referrer?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
    landing_page?: string;
    timestamp?: string;
  };
}

export const CustomAuth0Provider: FC<CustomAuth0ProviderProps> = ({
  service,
  children,
}) => {
  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

  const navigate = useNavigate();
  const { homePath } = useRouting();

  // TODO Consolidate redirectUri between different pages
  // Can we pass the final target as a parameter?
  let redirectUri: string;

  // Special handling for pricing route
  if (window.location.pathname === '/pricing') {
    redirectUri = `${window.location.origin}/pricing`;
  } else {
    // Use legacy redirect URIs for Auth0 to maintain compatibility
    // Auth0 needs to have these URLs registered as allowed callback URLs
    if (service === CirraServiceType.AGENTS) {
      redirectUri = `${window.location.origin}/admin-agent`;
    } else {
      // MCP uses root
      redirectUri = `${window.location.origin}/`;
    }
  }

  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID || '';

  if (!domain || !clientId) {
    trackException(new Error('Missing Auth0 configuration'), {
      domain: domain ? 'set' : 'missing',
      clientId: clientId ? 'set' : 'missing',
    });
    return (
      <div style={{ padding: '20px', color: 'red' }}>
        <h1>Configuration Error</h1>
        <p>
          Missing required Auth0 configuration. Please check your environment
          variables.
        </p>
      </div>
    );
  }

  // Handle invitations directly in the Auth0 provider
  const authParams = {
    redirect_uri: redirectUri,
    audience,
  };

  // Handle post-login logic here
  const onRedirectCallback = (appState: PostLoginState | undefined) => {
    console.log('[CustomAuth0Provider] onRedirectCallback called', {
      service,
      hasAppState: !!appState,
      hasSignupAttribution: !!appState?.signup_attribution,
      appState,
    });

    localStorage.removeItem(LS_KEY_CURRENT_ACCOUNT_ID);

    // For AGENTS service, navigate to the legacy path that's registered in Auth0
    if (service === CirraServiceType.AGENTS) {
      navigate(appState?.returnTo || '/admin-agent');
    } else {
      // For MCP service, use home path (root)
      navigate(appState?.returnTo || homePath);
    }
  };

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={authParams}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
    >
      <AuthErrorBoundary service={service}>{children}</AuthErrorBoundary>
    </Auth0Provider>
  );
};
